import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import s from '../../pages/PedidosOrcamentos.module.css';

const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtQtd   = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 });

const FORMAS_PGTO = ['Sem pagamento', 'Dinheiro', 'PIX', 'Boleto', 'Cheque', 'Cartão Crédito', 'Cartão Débito', 'Outro'];
const MOD_FRETE = [
  ['9', '9 — Sem ocorrência de transporte'],
  ['0', '0 — Por conta do emitente (CIF)'],
  ['1', '1 — Por conta do destinatário (FOB)'],
  ['2', '2 — Por conta de terceiros'],
  ['3', '3 — Próprio, por conta do remetente'],
  ['4', '4 — Próprio, por conta do destinatário'],
];
const HEADER_VAZIO = {
  processa_estoque: true, gera_financeiro: true, forma_pagamento: 'Sem pagamento',
  mod_frete: '9', transp_nome: '', transp_cnpj: '', transp_ie: '',
  transp_endereco: '', transp_municipio: '', transp_uf: '',
  veiculo_placa: '', veiculo_uf: '',
  vol_quantidade: '', vol_especie: '', peso_liquido: '', peso_bruto: '',
};

export default function ModalDevolucaoCompra({ entradaId, onClose }) {
  const [dados, setDados]         = useState(null);   // resultado do /preparar
  const [tmfId, setTmfId]         = useState('');
  const [qtds, setQtds]           = useState({});     // entrada_item_id → string
  const [header, setHeader]       = useState(HEADER_VAZIO);
  const [aba, setAba]             = useState('itens');
  const [rascunhoId, setRascunhoId] = useState(null);
  const [dirty, setDirty]         = useState(false);
  const [ocupado, setOcupado]     = useState(false);
  const [erro, setErro]           = useState('');
  const [gerada, setGerada]       = useState(null); // resposta de /confirmar (id, estoque/crédito)

  const setH = (k, v) => { setHeader(p => ({ ...p, [k]: v })); setDirty(true); };

  const carregar = useCallback(async (tmf) => {
    setErro('');
    try {
      const { data } = await api.get(`/devolucoes-compra/preparar/${entradaId}`,
        { params: tmf ? { tmf_id: tmf } : {} });
      setDados(data);
      setTmfId(String(data.tmf_selecionado.id));
      // Rascunho existente: carrega quantidades e cabeçalho salvos
      if (data.rascunho_id && !tmf) {
        const { data: r } = await api.get(`/devolucoes-compra/${data.rascunho_id}`);
        setRascunhoId(r.id);
        const q = {};
        (r.itens || []).forEach(it => { q[it.entrada_item_id] = String(it.quantidade); });
        setQtds(q);
        setHeader({
          ...HEADER_VAZIO,
          ...Object.fromEntries(Object.keys(HEADER_VAZIO).map(k => [k, r[k] ?? HEADER_VAZIO[k]])),
        });
        if (r.tipo_movimento_fiscal_id) {
          setTmfId(String(r.tipo_movimento_fiscal_id));
          const { data: d2 } = await api.get(`/devolucoes-compra/preparar/${entradaId}`,
            { params: { tmf_id: r.tipo_movimento_fiscal_id } });
          setDados(d2);
        }
      }
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar a entrada.');
    }
  }, [entradaId]);

  useEffect(() => { carregar(); }, [carregar]);

  const trocarLayout = async (novoTmf) => {
    setTmfId(novoTmf); setDirty(true);
    await carregar(novoTmf); // recalcula CFOP/CST convertidos
    setTmfId(novoTmf);
  };

  const devolverTodos = () => {
    const q = {};
    (dados?.itens || []).forEach(it => { if (it.disponivel > 0) q[it.entrada_item_id] = String(it.disponivel); });
    setQtds(q); setDirty(true);
  };

  const itensSelecionados = () => (dados?.itens || [])
    .map(it => ({ entrada_item_id: it.entrada_item_id, quantidade: parseFloat(qtds[it.entrada_item_id]) || 0 }))
    .filter(i => i.quantidade > 0);

  const totais = (dados?.itens || []).reduce((acc, it) => {
    const q = parseFloat(qtds[it.entrada_item_id]) || 0;
    if (q > 0) {
      acc.produtos += q * it.valor_unitario;
      acc.ipi      += it.valor_ipi_nf * (q / (it.quantidade_nf || 1));
    }
    return acc;
  }, { produtos: 0, ipi: 0 });

  const salvarRascunho = async () => {
    const itens = itensSelecionados();
    if (!itens.length) { setErro('Informe a quantidade de pelo menos um item.'); return null; }
    setOcupado(true); setErro('');
    try {
      const payload = { entrada_nf_id: entradaId, tipo_movimento_fiscal_id: parseInt(tmfId) || null, ...header, itens };
      const { data } = rascunhoId
        ? await api.put(`/devolucoes-compra/${rascunhoId}`, payload)
        : await api.post('/devolucoes-compra', payload);
      setRascunhoId(data.id);
      setDirty(false);
      return data.id;
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar a devolução.');
      return null;
    } finally { setOcupado(false); }
  };

  const confirmar = async () => {
    const id = await salvarRascunho();
    if (id) setErro('');
  };

  // "Gerar Devolução": sai do rascunho — mercadoria sai do estoque e o crédito
  // do fornecedor é gerado já aqui. Preview/Transmitir da NF-e ficam pra tela
  // de Entrada de NF, onde a devolução passa a aparecer pra consulta/navegação.
  const gerarDevolucao = async () => {
    const id = dirty || !rascunhoId ? await salvarRascunho() : rascunhoId;
    if (!id) return;
    if (!window.confirm(
      `Gerar esta devolução de compra?\n\n` +
      `• Total: ${fmtMoeda(totais.produtos + totais.ipi)}${totais.ipi > 0 ? ` (produtos ${fmtMoeda(totais.produtos)} + IPI devolvido ${fmtMoeda(totais.ipi)})` : ''}\n` +
      (header.processa_estoque ? `• A mercadoria devolvida SAI do estoque agora\n` : `• SEM movimentação de estoque\n`) +
      (header.gera_financeiro ? `• Gera CRÉDITO com o fornecedor agora\n` : `• SEM crédito de fornecedor\n`) +
      `\nA emissão da NF-e (Preview/Transmitir) fica disponível na tela de Entrada de NF, aba Devoluções.`
    )) return;
    setOcupado(true); setErro('');
    try {
      const { data } = await api.post(`/devolucoes-compra/${id}/confirmar`);
      setGerada(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao gerar a devolução.');
    } finally { setOcupado(false); }
  };

  if (!dados && !erro) return null;

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && !ocupado && onClose(gerada?.id || null)}>
      <div className={s.modal} style={{ width: 900, maxWidth: '97vw' }}>
        <div className={s.modalTitle}>
          📤 Devolução de Compra — Entrada #{dados?.entrada?.codigo} (NF {dados?.entrada?.numero_nf} — {dados?.entrada?.fornecedor_nome})
        </div>
        <div className={s.modalBody} style={{ gap: 8 }}>
          {erro && <div style={{ background: '#fef2f2', border: '1px solid #ef4444', borderRadius: 4, padding: '6px 10px', fontSize: 12, color: '#b91c1c' }}>{erro}</div>}

          {gerada ? (
            <div style={{ padding: 12, borderRadius: 6, background: '#ecfdf5', border: '1px solid #10b981' }}>
              <strong>✔ Devolução #{gerada.id} gerada</strong>
              <div style={{ fontSize: 11, marginTop: 4 }}>
                {gerada.estoque_movimentado ? '✓ Estoque baixado. ' : '— Estoque não movimentado. '}
                {gerada.credito_gerado ? '✓ Crédito gerado com o fornecedor.' : '— Sem crédito de fornecedor.'}
              </div>
              <div style={{ fontSize: 12, marginTop: 8 }}>
                Emita a NF-e (Preview/Transmitir) na tela de <strong>Entrada de NF → aba Devoluções</strong>.
              </div>
            </div>
          ) : (<>
            {/* Cabeçalho: layout de conversão + opções */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, minWidth: 280 }}>
                Layout de conversão (Tipo de Movimento Fiscal)
                <select className={s.selectErp} value={tmfId} onChange={e => trocarLayout(e.target.value)}>
                  {(dados?.tipos_movimento || []).map(t => (
                    <option key={t.id} value={t.id}>
                      {t.nome} ({t.cfop_dentro_estado_id}/{t.cfop_fora_estado_id})
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={header.processa_estoque}
                  onChange={e => setH('processa_estoque', e.target.checked)} />
                Processa estoque
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={header.gera_financeiro}
                  onChange={e => setH('gera_financeiro', e.target.checked)} />
                Gera financeiro (crédito fornecedor)
              </label>
            </div>

            {/* Abas */}
            <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid #c8d6e0' }}>
              {[['itens', 'Itens'], ['transporte', 'Transporte'], ['pagamento', 'Pagamento']].map(([k, l]) => (
                <button key={k} onClick={() => setAba(k)}
                  style={{
                    padding: '4px 14px', fontSize: 12, cursor: 'pointer', border: 'none', background: 'none',
                    borderBottom: aba === k ? '2px solid #2563eb' : '2px solid transparent',
                    color: aba === k ? '#2563eb' : '#555', fontWeight: aba === k ? 'bold' : 'normal', marginBottom: -2,
                  }}>{l}</button>
              ))}
            </div>

            {aba === 'itens' && (<>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#555' }}>
                  Quantidades iniciam zeradas — informe o que devolver ou use o botão ao lado.
                </span>
                <button className={s.btnErp} onClick={devolverTodos}>⤵ Devolver todos os itens</button>
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table className={s.itensGrid}>
                  <thead>
                    <tr>
                      <th>Descrição</th>
                      <th style={{ width: 42 }}>UN</th>
                      <th style={{ width: 78, textAlign: 'right' }}>Qtd. NF</th>
                      <th style={{ width: 78, textAlign: 'right' }}>Disponível</th>
                      <th style={{ width: 92, textAlign: 'right' }}>Devolver</th>
                      <th style={{ width: 90, textAlign: 'right' }}>Vlr. Unit.</th>
                      <th style={{ width: 60 }}>CFOP</th>
                      <th style={{ width: 88 }}>CFOP Conv.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dados?.itens || []).map(it => (
                      <tr key={it.entrada_item_id}
                        style={it.disponivel <= 0 ? { opacity: 0.5 } : undefined}>
                        <td title={`CST/CSOSN: ${it.csosn_convertido || '—'} · PIS: ${it.cst_pis_convertido || '—'} · COFINS: ${it.cst_cofins_convertido || '—'}`}>
                          {it.descricao}
                        </td>
                        <td style={{ textAlign: 'center' }}>{it.unidade}</td>
                        <td style={{ textAlign: 'right' }}>{fmtQtd(it.quantidade_nf)}</td>
                        <td style={{ textAlign: 'right', color: it.disponivel <= 0 ? '#b91c1c' : undefined }}>
                          {fmtQtd(it.disponivel)}
                        </td>
                        <td>
                          <input className={s.inputErp} type="number" min="0" step="0.001"
                            max={it.disponivel} disabled={it.disponivel <= 0}
                            style={{ width: '100%', textAlign: 'right' }}
                            value={qtds[it.entrada_item_id] ?? ''}
                            placeholder="0"
                            onChange={e => { setQtds(q => ({ ...q, [it.entrada_item_id]: e.target.value })); setDirty(true); }} />
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(it.valor_unitario)}</td>
                        <td style={{ textAlign: 'center', fontFamily: 'monospace' }}>{it.cfop_origem || '—'}</td>
                        <td style={{ textAlign: 'center', fontFamily: 'monospace', fontWeight: 'bold', color: '#1d4ed8' }}>
                          {it.cfop_convertido || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ textAlign: 'right', fontSize: 13 }}>
                Produtos: <strong>{fmtMoeda(totais.produtos)}</strong>
                {totais.ipi > 0 && <> · IPI devolvido: <strong>{fmtMoeda(totais.ipi)}</strong></>}
                {' '}· Total: <strong style={{ fontSize: 14 }}>{fmtMoeda(totais.produtos + totais.ipi)}</strong>
              </div>
            </>)}

            {aba === 'transporte' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, fontSize: 12 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, gridColumn: 'span 2' }}>
                  Modalidade do frete
                  <select className={s.selectErp} value={header.mod_frete} onChange={e => setH('mod_frete', e.target.value)}>
                    {MOD_FRETE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, gridColumn: 'span 2' }}>
                  Transportadora
                  <input className={s.inputErp} value={header.transp_nome} disabled={header.mod_frete === '9'}
                    onChange={e => setH('transp_nome', e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  CNPJ/CPF
                  <input className={s.inputErp} value={header.transp_cnpj} disabled={header.mod_frete === '9'}
                    onChange={e => setH('transp_cnpj', e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  IE
                  <input className={s.inputErp} value={header.transp_ie} disabled={header.mod_frete === '9'}
                    onChange={e => setH('transp_ie', e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, gridColumn: 'span 2' }}>
                  Endereço
                  <input className={s.inputErp} value={header.transp_endereco} disabled={header.mod_frete === '9'}
                    onChange={e => setH('transp_endereco', e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  Município
                  <input className={s.inputErp} value={header.transp_municipio} disabled={header.mod_frete === '9'}
                    onChange={e => setH('transp_municipio', e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  UF
                  <input className={s.inputErp} maxLength={2} value={header.transp_uf} disabled={header.mod_frete === '9'}
                    onChange={e => setH('transp_uf', e.target.value.toUpperCase())} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  Placa do veículo
                  <input className={s.inputErp} value={header.veiculo_placa} disabled={header.mod_frete === '9'}
                    onChange={e => setH('veiculo_placa', e.target.value.toUpperCase())} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  UF da placa
                  <input className={s.inputErp} maxLength={2} value={header.veiculo_uf} disabled={header.mod_frete === '9'}
                    onChange={e => setH('veiculo_uf', e.target.value.toUpperCase())} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  Qtd. volumes
                  <input className={s.inputErp} type="number" min="0" value={header.vol_quantidade}
                    onChange={e => setH('vol_quantidade', e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  Espécie
                  <input className={s.inputErp} value={header.vol_especie} placeholder="Ex.: caixa, fardo…"
                    onChange={e => setH('vol_especie', e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  Peso líquido (kg)
                  <input className={s.inputErp} type="number" min="0" step="0.001" value={header.peso_liquido}
                    onChange={e => setH('peso_liquido', e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  Peso bruto (kg)
                  <input className={s.inputErp} type="number" min="0" step="0.001" value={header.peso_bruto}
                    onChange={e => setH('peso_bruto', e.target.value)} />
                </label>
              </div>
            )}

            {aba === 'pagamento' && (
              <div style={{ maxWidth: 380 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
                  Forma de pagamento da devolução
                  <select className={s.selectErp} value={header.forma_pagamento}
                    onChange={e => setH('forma_pagamento', e.target.value)}>
                    {FORMAS_PGTO.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </label>
                <p style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                  Em devoluções o padrão é <strong>Sem pagamento</strong> (tPag 90): quem acerta o valor
                  é o financeiro, via crédito com o fornecedor. Escolha outra forma apenas se houver
                  acerto em dinheiro na operação.
                </p>
              </div>
            )}
          </>)}
        </div>
        <div className={s.modalFooter}>
          {gerada ? (
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={() => onClose(gerada?.id || null)}>Concluir</button>
          ) : (<>
            <button className={s.btnErp} disabled={ocupado} onClick={confirmar}
              title="Salva a devolução como rascunho (pode fechar e continuar depois)">
              {ocupado ? '…' : dirty || !rascunhoId ? '💾 Salvar Rascunho' : '💾 Salvo'}
            </button>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={ocupado} onClick={gerarDevolucao}
              title="Gera a devolução: baixa o estoque e o crédito do fornecedor agora. A emissão da NF-e fica na tela de Entrada de NF.">
              {ocupado ? 'Gerando…' : '✅ Gerar Devolução'}
            </button>
            <button className={s.btnErp} disabled={ocupado} onClick={() => onClose(null)}>Fechar</button>
          </>)}
        </div>
      </div>
    </div>
  );
}
